class CartDrawer {
    constructor() {
        this.drawer = document.querySelector('[data-cart-drawer]');
        if (!this.drawer) {
            console.error('Cart drawer element not found');
            return;
        }

        this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
        this.closeButtons = this.drawer.querySelectorAll('[data-cart-drawer-close]');
        this.form = this.drawer.querySelector('[data-cart-form]');
        this.cartItems = this.drawer.querySelector('[data-cart-items]');
        this.cartEmpty = this.drawer.querySelector('[data-cart-empty]');
        this.subtotal = this.drawer.querySelector('[data-cart-subtotal]');
        this.discounts = this.drawer.querySelector('[data-cart-discounts]');
        this.recommendations = this.drawer.querySelector('[data-cart-recommendations]');
        this.recommendationsContent = this.drawer.querySelector('[data-cart-recommendations-content]');
        this.sliderPrevBtn = this.drawer.querySelector('[data-slider-prev]');
        this.sliderNextBtn = this.drawer.querySelector('[data-slider-next]');

        // Slider state
        this.currentSlide = 0;
        this.totalSlides = 0;

        // Get settings from data attributes or defaults
        this.settings = {
            notificationPosition: this.drawer.dataset.notificationPosition || 'top-right',
            notificationDuration: parseInt(this.drawer.dataset.notificationDuration || '3') * 1000
        };

        // Queue system to prevent 409 conflicts
        this.updateQueue = Promise.resolve();
        this.isUpdating = false;

        // Debounce timers for quantity updates
        this.quantityTimers = {};

        this.init();
    }

    init() {
        this.bindEvents();
        this.interceptAddToCart();

        // Also handle manual drawer opens
        document.addEventListener('click', (e) => {
            if (e.target.closest('[data-cart-drawer-open]')) {
                e.preventDefault();
                this.refreshCart().then(() => this.open());
            }
        });
    }

    bindEvents() {
        // Close drawer
        this.closeButtons.forEach(button => {
            button.addEventListener('click', () => this.close());
        });

        // Close on overlay click
        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
        }

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
                this.close();
            }
        });

        // Slider controls
        if (this.sliderPrevBtn) {
            this.sliderPrevBtn.addEventListener('click', () => this.slideRecommendations('prev'));
        }
        if (this.sliderNextBtn) {
            this.sliderNextBtn.addEventListener('click', () => this.slideRecommendations('next'));
        }

        // Cart item events
        if (this.cartItems) {
            this.cartItems.addEventListener('click', (e) => {
                const removeBtn = e.target.closest('[data-cart-item-remove]');
                const increaseBtn = e.target.closest('[data-cart-item-increase]');
                const decreaseBtn = e.target.closest('[data-cart-item-decrease]');

                if (removeBtn) {
                    e.preventDefault();
                    this.removeItem(removeBtn);
                } else if (increaseBtn) {
                    e.preventDefault();
                    this.changeQuantity(increaseBtn, 1);
                } else if (decreaseBtn) {
                    e.preventDefault();
                    this.changeQuantity(decreaseBtn, -1);
                }
            });

            // Quantity input change
            this.cartItems.addEventListener('change', (e) => {
                if (e.target.matches('[data-cart-item-quantity]')) {
                    this.updateQuantity(e.target);
                }
            });
        }

        // Cart note auto-save
        const noteField = this.drawer.querySelector('#CartDrawerNote');
        if (noteField) {
            let noteTimeout;
            noteField.addEventListener('input', () => {
                clearTimeout(noteTimeout);
                noteTimeout = setTimeout(() => {
                    this.updateNote(noteField.value);
                }, 1000);
            });
        }
    }

    interceptAddToCart() {
        // 1. Intercept ALL form submissions to /cart/add
        document.addEventListener('submit', (e) => {
            const form = e.target;
            const action = form.getAttribute('action') || form.action || '';

            if (action.includes('/cart/add') || action === '/cart/add') {
                e.preventDefault();
                e.stopPropagation();
                console.log('Add to cart form intercepted');
                this.addToCart(form);
            }
        }, true);

        // 2. Intercept button clicks
        document.addEventListener('click', (e) => {
            const target = e.target;
            const button = target.closest('button[name="add"], button[type="submit"], [data-ajax-add-to-cart]');

            if (button) {
                const form = button.closest('form');
                if (form) {
                    const action = form.getAttribute('action') || form.action || '';
                    if (action.includes('/cart/add') || action === '/cart/add') {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Add to cart button intercepted');
                        this.addToCart(form);
                    }
                }
            }
        }, true);

        // 3. Override Shopify's default cart behavior
        if (window.Shopify && window.Shopify.addItem) {
            const originalAddItem = window.Shopify.addItem;
            window.Shopify.addItem = async (variantId, quantity = 1, properties = {}) => {
                console.log('Shopify.addItem intercepted');
                try {
                    const formData = new FormData();
                    formData.append('id', variantId);
                    formData.append('quantity', quantity);

                    Object.entries(properties).forEach(([key, value]) => {
                        formData.append(`properties[${key}]`, value);
                    });

                    const response = await fetch('/cart/add.js', {
                        method: 'POST',
                        body: formData
                    });

                    const data = await response.json();

                    if (!response.ok) {
                        throw new Error(data.description || 'Failed to add to cart');
                    }

                    await this.refreshCart();
                    this.open();
                    this.showNotification('Item added to cart');

                    return data;
                } catch (error) {
                    console.error('Error in Shopify.addItem:', error);
                    this.showNotification(error.message || 'Error adding item to cart', 'error');
                    throw error;
                }
            };
        }

        // 4. Intercept direct fetch calls
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const url = typeof args[0] === 'string' ? args[0] : args[0].url;

            if (url.includes('/cart/add.js') || url === '/cart/add.js') {
                console.log('Direct fetch to /cart/add.js intercepted');
                const response = await originalFetch(...args);
                const clonedResponse = response.clone();

                if (response.ok) {
                    clonedResponse.json().then(() => {
                        this.refreshCart();
                        this.open();
                        this.showNotification('Item added to cart');
                    }).catch(err => console.error('Error processing cart add:', err));
                }

                return response;
            }

            return originalFetch(...args);
        };
    }

    async addToCart(form) {
        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');

        if (submitButton) {
            submitButton.disabled = true;
            submitButton.classList.add('loading');
        }

        try {
            console.log('Sending add to cart request...');

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.description || 'Failed to add to cart');
            }

            console.log('Item added to cart successfully:', data);

            await this.refreshCart();
            this.open();
            this.showNotification('Item added to cart');

        } catch (error) {
            console.error('Error adding to cart:', error);
            this.showNotification(error.message || 'Error adding item to cart', 'error');
        } finally {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.classList.remove('loading');
            }
        }
    }

    async refreshCart() {
        try {
            console.log('Fetching cart data...');
            const response = await fetch('/cart.js');
            const cart = await response.json();

            console.log('Cart data:', cart);

            await this.renderCart(cart);
            this.updateCartCount(cart.item_count);

        } catch (error) {
            console.error('Error refreshing cart:', error);
        }
    }

    async renderCart(cart) {
        if (cart.item_count === 0) {
            if (this.cartEmpty) this.cartEmpty.style.display = 'flex';
            if (this.form) this.form.style.display = 'none';
            if (this.recommendations) this.recommendations.style.display = 'none';
            return;
        }

        if (this.cartEmpty) this.cartEmpty.style.display = 'none';
        if (this.form) this.form.style.display = 'block';

        if (this.cartItems) {
            const itemsHTML = cart.items.map(item => this.renderCartItem(item)).join('');
            this.cartItems.innerHTML = itemsHTML;
        }

        if (this.subtotal) {
            this.subtotal.textContent = this.formatMoney(cart.total_price);
        }

        this.renderDiscounts(cart);

        // Load recommendations based on first product
        if (cart.items.length > 0) {
            this.loadRecommendations(cart.items[0].product_id);
        }
    }

    renderCartItem(item) {
        const image = item.image ?
            `<img src="${item.image}" alt="${item.title}" loading="lazy">` :
            '<div class="placeholder-svg"></div>';

        const variant = item.variant_title && item.variant_title !== 'Default Title' ?
            `<div class="cart-drawer-item__variant">${item.variant_title}</div>` : '';

        const properties = item.properties && Object.keys(item.properties).length > 0 ?
            this.renderProperties(item.properties) : '';

        const discounts = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0 ?
            this.renderItemDiscounts(item.line_level_discount_allocations) : '';

        const priceHTML = item.original_line_price !== item.final_line_price ?
            `<span class="cart-drawer-item__price-original">${this.formatMoney(item.original_line_price)}</span>
       <span class="cart-drawer-item__price-final">${this.formatMoney(item.final_line_price)}</span>` :
            `<span class="cart-drawer-item__price-final">${this.formatMoney(item.final_line_price)}</span>`;

        return `
      <div class="cart-drawer-item" data-cart-item data-cart-item-key="${item.key}">
        <div class="cart-drawer-item__image">
          ${image}
        </div>
        <div class="cart-drawer-item__details">
          <div class="cart-drawer-item__info">
            <a href="${item.url}" class="cart-drawer-item__title">${item.product_title}</a>
            ${variant}
            ${properties}
            ${discounts}
          </div>
          <div class="cart-drawer-item__footer">
            <div class="cart-drawer-item__quantity">
              <button type="button" class="cart-drawer-item__qty-btn" data-cart-item-decrease aria-label="Decrease quantity">
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none">
                  <path d="M0 1H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <input 
                type="number" 
                class="cart-drawer-item__qty-input" 
                value="${item.quantity}" 
                min="0" 
                data-cart-item-quantity
                data-cart-item-key="${item.key}"
                aria-label="Quantity"
              >
              <button type="button" class="cart-drawer-item__qty-btn" data-cart-item-increase aria-label="Increase quantity">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
            <div class="cart-drawer-item__price">
              ${priceHTML}
            </div>
          </div>
          <button type="button" class="cart-drawer-item__remove" data-cart-item-remove data-cart-item-key="${item.key}" aria-label="Remove item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/>
            </svg>
          </button>
        </div>
      </div>
    `;
    }

    renderProperties(properties) {
        const props = Object.entries(properties)
            .filter(([key, value]) => !key.startsWith('_') && value)
            .map(([key, value]) => `
        <div class="cart-drawer-item__property">
          <span>${key}:</span>
          <span>${value}</span>
        </div>
      `).join('');

        return props ? `<div class="cart-drawer-item__properties">${props}</div>` : '';
    }

    renderItemDiscounts(discounts) {
        const discountsHTML = discounts.map(discount => `
      <div class="cart-drawer-item__discount">
        ${discount.discount_application.title} (-${this.formatMoney(discount.amount)})
      </div>
    `).join('');

        return `<div class="cart-drawer-item__discounts">${discountsHTML}</div>`;
    }

    renderDiscounts(cart) {
        if (!this.discounts) return;

        if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) {
            this.discounts.innerHTML = '';
            return;
        }

        const discountsHTML = cart.cart_level_discount_applications.map(discount => `
      <div class="cart-drawer__discount">
        <span>${discount.title}</span>
        <span>-${this.formatMoney(discount.total_allocated_amount)}</span>
      </div>
    `).join('');

        this.discounts.innerHTML = discountsHTML;
    }

    async changeQuantity(button, change) {
        const item = button.closest('[data-cart-item]');
        const input = item.querySelector('[data-cart-item-quantity]');
        const key = input.dataset.cartItemKey;
        const newQuantity = parseInt(input.value) + change;

        if (newQuantity < 0) return;

        input.value = newQuantity;

        if (this.quantityTimers[key]) {
            clearTimeout(this.quantityTimers[key]);
        }

        this.quantityTimers[key] = setTimeout(() => {
            console.log(`Debounced update for ${key}: ${newQuantity}`);
            this.queueUpdate(() => this.updateQuantityDirect(input));
            delete this.quantityTimers[key];
        }, 500);
    }

    async updateQuantity(input) {
        const key = input.dataset.cartItemKey;

        if (this.quantityTimers[key]) {
            clearTimeout(this.quantityTimers[key]);
        }

        this.quantityTimers[key] = setTimeout(() => {
            console.log(`Debounced update for ${key}: ${input.value}`);
            this.queueUpdate(() => this.updateQuantityDirect(input));
            delete this.quantityTimers[key];
        }, 500);
    }

    queueUpdate(updateFunction) {
        this.updateQueue = this.updateQueue.then(() => updateFunction()).catch(err => {
            console.error('Queue error:', err);
        });
    }

    async updateQuantityDirect(input) {
        if (this.isUpdating) return;

        const key = input.dataset.cartItemKey;
        const quantity = parseInt(input.value);

        if (quantity < 0) return;

        this.isUpdating = true;

        // Find the cart item and add loading state
        const item = input.closest('[data-cart-item]');
        if (item) {
            item.classList.add('is-loading');
        }

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity })
            });

            if (!response.ok) throw new Error('Failed to update quantity');

            await this.refreshCart();
        } catch (error) {
            console.error('Error updating quantity:', error);
            this.showNotification('Error updating quantity', 'error');
        } finally {
            this.isUpdating = false;
            // Remove loading state after refresh
            if (item) {
                item.classList.remove('is-loading');
            }
        }
    }

    async removeItem(button) {
        const key = button.dataset.cartItemKey;
        const item = button.closest('[data-cart-item]');

        this.queueUpdate(async () => {
            // Add loading state to the item being removed
            if (item) {
                item.classList.add('is-loading');
            }

            try {
                const response = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: key, quantity: 0 })
                });

                if (!response.ok) throw new Error('Failed to remove item');

                await this.refreshCart();
                this.showNotification('Item removed from cart');
            } catch (error) {
                console.error('Error removing item:', error);
                this.showNotification('Error removing item', 'error');
                // Remove loading state on error
                if (item) {
                    item.classList.remove('is-loading');
                }
            }
        });
    }

    async updateNote(note) {
        try {
            await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note })
            });
        } catch (error) {
            console.error('Error updating note:', error);
        }
    }

    updateCartCount(count) {
        const cartCounts = document.querySelectorAll('[data-cart-count]');
        cartCounts.forEach(el => {
            el.textContent = count;
            if (count > 0) {
                el.style.display = '';
                el.classList.remove('hidden');
            } else {
                el.style.display = 'none';
                el.classList.add('hidden');
            }
        });
    }

    open() {
        console.log('Opening cart drawer');
        this.drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        const closeButton = this.drawer.querySelector('[data-cart-drawer-close]');
        if (closeButton) {
            setTimeout(() => closeButton.focus(), 100);
        }
    }

    close() {
        console.log('Closing cart drawer');
        this.drawer.classList.remove('is-open');
        document.body.style.overflow = '';
    }

    formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
    }

    async loadRecommendations(productId) {
        if (!this.recommendations || !this.recommendationsContent) return;

        try {
            // Use Shopify's product recommendations API
            const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6`);

            if (!response.ok) {
                throw new Error('Failed to load recommendations');
            }

            const data = await response.json();

            if (data.products && data.products.length > 0) {
                // Render product cards
                const productsHTML = data.products.map(product => this.renderRecommendationItem(product)).join('');
                this.recommendationsContent.innerHTML = productsHTML;
                this.recommendations.style.display = 'block';

                // Initialize slider
                this.totalSlides = data.products.length;
                this.currentSlide = 0;
                this.updateSliderButtons();

                // Bind add to cart buttons for recommendations
                this.bindRecommendationEvents();
            } else {
                this.recommendations.style.display = 'none';
            }
        } catch (error) {
            console.error('Error loading recommendations:', error);
            this.recommendations.style.display = 'none';
        }
    }

    bindRecommendationEvents() {
        if (!this.recommendationsContent) return;

        this.recommendationsContent.addEventListener('click', async (e) => {
            const addBtn = e.target.closest('[data-recommendation-add]');
            if (addBtn && !addBtn.disabled) {
                e.preventDefault();
                const variantId = addBtn.dataset.variantId;
                await this.addRecommendationToCart(variantId, addBtn);
            }
        });
    }

    async addRecommendationToCart(variantId, button) {
        button.disabled = true;
        button.classList.add('loading');

        const originalText = button.innerHTML;
        button.innerHTML = '<span>Adding...</span>';

        try {
            const formData = new FormData();
            formData.append('id', variantId);
            formData.append('quantity', 1);

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.description || 'Failed to add to cart');
            }

            await this.refreshCart();
            this.showNotification('Item added to cart');

        } catch (error) {
            console.error('Error adding recommendation to cart:', error);
            this.showNotification(error.message || 'Error adding item to cart', 'error');
            button.innerHTML = originalText;
            button.disabled = false;
            button.classList.remove('loading');
        }
    }

    slideRecommendations(direction) {
        if (direction === 'next' && this.currentSlide < this.totalSlides - 2) {
            this.currentSlide++;
        } else if (direction === 'prev' && this.currentSlide > 0) {
            this.currentSlide--;
        }

        const slideWidth = this.recommendationsContent.querySelector('.product-item').offsetWidth;
        const gap = 12;
        const offset = -(this.currentSlide * (slideWidth + gap));

        this.recommendationsContent.style.transform = `translateX(${offset}px)`;
        this.updateSliderButtons();
    }

    updateSliderButtons() {
        if (!this.sliderPrevBtn || !this.sliderNextBtn) return;

        this.sliderPrevBtn.disabled = this.currentSlide === 0;
        this.sliderNextBtn.disabled = this.currentSlide >= this.totalSlides - 2;

        this.sliderPrevBtn.style.opacity = this.currentSlide === 0 ? '0.3' : '1';
        this.sliderNextBtn.style.opacity = this.currentSlide >= this.totalSlides - 2 ? '0.3' : '1';
    }

    renderRecommendationItem(product) {
        const image = product.featured_image ?
            `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">` :
            '<div class="placeholder-svg"></div>';

        const price = product.compare_at_price_min > product.price_min ?
            `<p class="product-item__price sale">
                <span class="money">${this.formatMoney(product.price_min)}</span>
                <span class="product-item__price-was money">${this.formatMoney(product.compare_at_price_min)}</span>
            </p>` :
            `<p class="product-item__price"><span class="money">${this.formatMoney(product.price_min)}</span></p>`;

        // Get first available variant
        const variantId = product.variants && product.variants.length > 0 ? product.variants[0].id : null;
        const isAvailable = product.available;

        const addToCartButton = variantId && isAvailable ?
            `<button type="button" class="product-item__add-btn" data-recommendation-add data-variant-id="${variantId}">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 3H4.5L6.5 12H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M6.5 9H13L14.5 4.5H5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="7.5" cy="14" r="0.75" fill="currentColor"/>
                    <circle cx="12.5" cy="14" r="0.75" fill="currentColor"/>
                </svg>
                <span>Add to Cart</span>
            </button>` :
            `<button type="button" class="product-item__add-btn product-item__add-btn--sold-out" disabled>
                <span>Sold Out</span>
            </button>`;

        return `
            <article class="product-item">
                <div class="product-item__image">
                    <a href="${product.url}" class="product-item__image-link" aria-label="${product.title}">
                        ${image}
                    </a>
                </div>
                <div class="product-item__info">
                    <h2 class="product-item__title">
                        <a href="${product.url}">${product.title}</a>
                    </h2>
                    ${price}
                    ${addToCartButton}
                </div>
            </article>
        `;
    }

    showNotification(message, type = 'success') {
        console.log(`Notification: ${message} (${type})`);

        const existing = document.querySelector('.cart-notification');
        if (existing) existing.remove();

        const notification = document.createElement('div');
        notification.className = `cart-notification cart-notification--${type}`;
        notification.setAttribute('data-position', this.settings.notificationPosition);
        notification.textContent = message;

        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.add('is-closing');
            setTimeout(() => notification.remove(), 300);
        }, this.settings.notificationDuration);
    }
}

// Initialize cart drawer when DOM is ready
let cartDrawerInstance;

function initCartDrawer() {
    console.log('Initializing cart drawer...');
    cartDrawerInstance = new CartDrawer();
    window.cartDrawer = cartDrawerInstance;
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
} else {
    initCartDrawer();
}